import React, { useState, useRef, DragEvent, ChangeEvent } from 'react';
import { bulkImportProducts, BulkImportResponse } from '../../services/productService';

interface BulkImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function BulkImportModal({ isOpen, onClose, onSuccess }: BulkImportModalProps) {
  const [dragActive, setDragActive] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BulkImportResponse | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleDrag = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const validateAndSetFile = (selectedFile: File) => {
    setError(null);
    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const fileName = selectedFile.name.toLowerCase();
    const isValid = validExtensions.some(ext => fileName.endsWith(ext));

    if (!isValid) {
      setError("Unsupported file format. Please upload an Excel (.xlsx, .xls) or CSV (.csv) file.");
      setFile(null);
      return;
    }
    setFile(selectedFile);
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateAndSetFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      validateAndSetFile(e.target.files[0]);
    }
  };

  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleUpload = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await bulkImportProducts(file);
      setResult(response);
      if (response.successCount > 0) {
        onSuccess();
      }
    } catch (err: any) {
      setError(err?.message || "An error occurred while uploading the file. Please verify file content and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setResult(null);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 500,
      background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
    }} onClick={onClose}>
      <style>{`
        @keyframes modalIn { from { opacity: 0; transform: scale(0.97); } to { opacity: 1; transform: scale(1); } }
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        .import-row { transition: background 0.1s; border-bottom: 1px solid var(--border); }
        .import-row:hover { background: rgba(255, 255, 255, 0.02); }
      `}</style>
      
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "var(--card-solid)", border: "1px solid var(--border-strong)",
        borderRadius: 16, width: 680, maxWidth: "95vw", maxHeight: "85vh",
        overflow: "hidden", display: "flex", flexDirection: "column",
        animation: "modalIn 0.2s ease",
      }}>
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "flex-start", justifyContent: "space-between",
          padding: "20px 24px", borderBottom: "1px solid var(--border)",
        }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", color: "var(--or)", marginBottom: 4 }}>
              Bulk Actions
            </div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: "var(--text)" }}>Bulk Import Products</h2>
          </div>
          <button onClick={onClose} style={{
            width: 30, height: 30, borderRadius: 7, border: "1px solid var(--border)",
            background: "transparent", color: "var(--text3)", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0,
            transition: "all 0.15s",
          }} onMouseEnter={(e) => e.currentTarget.style.borderColor = "var(--border-strong)"}
             onMouseLeave={(e) => e.currentTarget.style.borderColor = "var(--border)"}>
            ×
          </button>
        </div>

        {/* Content Body */}
        <div style={{ overflowY: "auto", padding: "24px", display: "flex", flexDirection: "column", gap: 20 }}>
          
          {/* Top Level Error Banner */}
          {error && (
            <div style={{
              background: "rgba(224,82,82,0.1)", border: "1px solid rgba(224,82,82,0.25)",
              borderRadius: 8, padding: "12px 16px", display: "flex", gap: 10, alignItems: "center",
            }}>
              <span style={{ color: "#e05252", fontSize: 16, fontWeight: "bold" }}>✕</span>
              <span style={{ color: "#e08b8b", fontSize: 13, lineHeight: 1.5 }}>{error}</span>
            </div>
          )}

          {/* Phase 1: File Uploading Loading Overlay */}
          {loading && (
            <div style={{
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              padding: "48px 24px", gap: 16,
            }}>
              <div style={{
                width: 42, height: 42, border: "3px solid var(--border)", borderTop: "3px solid var(--or)",
                borderRadius: "50%", animation: "spin 0.8s linear infinite"
              }} />
              <div style={{ fontSize: 14, fontWeight: 500, color: "var(--text2)" }}>Processing import file...</div>
              <div style={{ fontSize: 12, color: "var(--text3)" }}>Validating data rules, slugs and database rows. Please wait.</div>
            </div>
          )}

          {/* Phase 2: Selection / Dropzone */}
          {!loading && !result && (
            <>
              {/* Drag/Drop area if no file is selected */}
              {!file ? (
                <div 
                  onDragEnter={handleDrag}
                  onDragOver={handleDrag}
                  onDragLeave={handleDrag}
                  onDrop={handleDrop}
                  onClick={triggerFileSelect}
                  style={{
                    border: `2px dashed ${dragActive ? 'var(--or)' : 'var(--border)'}`,
                    background: dragActive ? 'var(--or-dim)' : 'rgba(255,255,255,0.01)',
                    borderRadius: 12, padding: "40px 24px", textAlign: "center", cursor: "pointer",
                    transition: "all 0.15s ease", display: "flex", flexDirection: "column", alignItems: "center",
                    gap: 12,
                  }}
                >
                  <input 
                    ref={fileInputRef} 
                    type="file" 
                    onChange={handleFileChange} 
                    accept=".xlsx,.xls,.csv" 
                    style={{ display: "none" }}
                  />
                  <div style={{ fontSize: 32, opacity: 0.7 }}>📥</div>
                  <div>
                    <span style={{ fontSize: 14, fontWeight: 500, color: "var(--text2)" }}>
                      Drag and drop your file here, or <span style={{ color: "var(--or)", textDecoration: "underline" }}>browse</span>
                    </span>
                    <p style={{ margin: "6px 0 0", fontSize: 12, color: "var(--text3)" }}>
                      Supports Excel (.xlsx, .xls) and CSV (.csv) format files up to 100 products.
                    </p>
                  </div>
                </div>
              ) : (
                /* Selected File Card */
                <div style={{
                  background: "var(--input-bg)", border: "1px solid var(--border)",
                  borderRadius: 10, padding: "16px 20px", display: "flex", alignItems: "center",
                  justifyContent: "space-between",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ fontSize: 24 }}>📄</div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 500, color: "var(--text2)", wordBreak: "break-all" }}>{file.name}</div>
                      <div style={{ fontSize: 12, color: "var(--text3)", marginTop: 2 }}>{formatBytes(file.size)}</div>
                    </div>
                  </div>
                  <button onClick={handleReset} style={{
                    background: "transparent", border: "1px solid var(--border)",
                    borderRadius: 6, color: "var(--text3)", cursor: "pointer",
                    padding: "6px 12px", fontSize: 12, fontFamily: "'DM Sans', sans-serif",
                    transition: "all 0.15s",
                  }} onMouseEnter={(e) => e.currentTarget.style.borderColor = "var(--border-strong)"}
                     onMouseLeave={(e) => e.currentTarget.style.borderColor = "var(--border)"}>
                    Change File
                  </button>
                </div>
              )}

              {/* Template download link */}
              <div style={{
                background: "rgba(255,255,255,0.02)", border: "1px solid var(--border)",
                borderRadius: 8, padding: "12px 16px", display: "flex", justifyContent: "space-between",
                alignItems: "center",
              }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span style={{ fontSize: 16 }}>📋</span>
                  <span style={{ fontSize: 13, color: "var(--text3)" }}>Need the standard import format template?</span>
                </div>
                <a 
                  href="/bulk_import_sample.xlsx" 
                  download="bulk_import_sample.xlsx" 
                  style={{
                    fontSize: 13, color: "var(--or)", textDecoration: "none", fontWeight: 500,
                    display: "inline-flex", alignItems: "center", gap: 4
                  }}
                >
                  Download template ↓
                </a>
              </div>
            </>
          )}

          {/* Phase 3: Results Display */}
          {!loading && result && (
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              
              {/* Summary Stats */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
                {[
                  { label: "Total Rows", value: result.totalRows, color: "var(--text)", bg: "var(--input-bg)" },
                  { label: "Successful", value: result.successCount, color: "#6ddc6d", bg: "rgba(109,220,109,0.06)", border: "rgba(109,220,109,0.15)" },
                  { label: "Failed", value: result.failureCount, color: "#e05252", bg: "rgba(224,82,82,0.06)", border: "rgba(224,82,82,0.15)" },
                ].map((stat, idx) => (
                  <div key={idx} style={{
                    background: stat.bg,
                    border: stat.border ? `1px solid ${stat.border}` : "1px solid var(--border)",
                    borderRadius: 10, padding: "14px 16px", textAlign: "center",
                  }}>
                    <div style={{ fontSize: 11, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>{stat.label}</div>
                    <div style={{ fontSize: 24, fontWeight: 600, color: stat.color }}>{stat.value}</div>
                  </div>
                ))}
              </div>

              {/* Detail Table */}
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text2)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>
                  Import Validation Details
                </div>
                <div style={{ 
                  border: "1px solid var(--border)", borderRadius: 10, 
                  overflow: "hidden", background: "var(--card)"
                }}>
                  <div style={{ maxHeight: 240, overflowY: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, textAlign: "left" }}>
                      <thead>
                        <tr style={{ background: "rgba(255,255,255,0.02)", borderBottom: "1px solid var(--border)" }}>
                          <th style={{ padding: "8px 12px", width: 60, color: "var(--text3)", fontWeight: 500 }}>Row</th>
                          <th style={{ padding: "8px 12px", width: 150, color: "var(--text3)", fontWeight: 500 }}>Product Key</th>
                          <th style={{ padding: "8px 12px", width: 90, color: "var(--text3)", fontWeight: 500 }}>Status</th>
                          <th style={{ padding: "8px 12px", color: "var(--text3)", fontWeight: 500 }}>Report / Error Message</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.results.map((row, i) => (
                          <tr key={i} className="import-row">
                            <td style={{ padding: "8px 12px", color: "var(--text3)" }}>{row.row}</td>
                            <td style={{ padding: "8px 12px", color: "var(--text2)", fontWeight: 500, fontFamily: "monospace" }}>{row.product_key}</td>
                            <td style={{ padding: "8px 12px" }}>
                              <span style={{
                                display: "inline-flex", alignItems: "center", gap: 4,
                                fontSize: 11, fontWeight: 600,
                                color: row.status === 'success' ? '#6ddc6d' : '#e05252',
                              }}>
                                <span style={{
                                  width: 5, height: 5, borderRadius: "50%",
                                  background: row.status === 'success' ? '#6ddc6d' : '#e05252'
                                }} />
                                {row.status === 'success' ? 'Succeeded' : 'Failed'}
                              </span>
                            </td>
                            <td style={{ 
                              padding: "8px 12px", 
                              color: row.status === 'success' ? 'var(--text3)' : '#e08b8b',
                              fontSize: 12
                            }}>
                              {row.status === 'success' ? `Successfully imported. Product ID: ${row.productId}` : row.message}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

            </div>
          )}

        </div>

        {/* Footer Actions */}
        <div style={{
          padding: "16px 24px", borderTop: "1px solid var(--border)",
          display: "flex", gap: 10, justifyContent: "flex-end", background: "rgba(0,0,0,0.15)",
        }}>
          {result ? (
            <>
              <button onClick={handleReset} style={{
                padding: "9px 18px", borderRadius: 8, border: "1px solid var(--border)",
                background: "transparent", color: "var(--text2)", fontSize: 13, cursor: "pointer",
                fontFamily: "'DM Sans', sans-serif", transition: "all 0.15s",
              }} onMouseEnter={(e) => e.currentTarget.style.borderColor = "var(--border-strong)"}
                 onMouseLeave={(e) => e.currentTarget.style.borderColor = "var(--border)"}>
                Import Another File
              </button>
              <button onClick={onClose} style={{
                padding: "9px 18px", borderRadius: 8, border: "none",
                background: "linear-gradient(135deg, #BF3D0C, #E8593C)",
                color: "#fff", fontSize: 13, fontWeight: 500, cursor: "pointer",
                fontFamily: "'DM Sans', sans-serif", boxShadow: "0 2px 10px rgba(191,61,12,0.2)",
              }}>
                Done & Close
              </button>
            </>
          ) : (
            <>
              <button onClick={onClose} style={{
                padding: "9px 18px", borderRadius: 8, border: "1px solid var(--border)",
                background: "transparent", color: "var(--text2)", fontSize: 13, cursor: "pointer",
                fontFamily: "'DM Sans', sans-serif",
              }}>
                Cancel
              </button>
              <button 
                onClick={handleUpload} 
                disabled={!file || loading}
                style={{
                  padding: "9px 18px", borderRadius: 8, border: "none",
                  background: (!file || loading) ? "rgba(255,255,255,0.06)" : "linear-gradient(135deg, #BF3D0C, #E8593C)",
                  color: (!file || loading) ? "var(--text3)" : "#fff", 
                  fontSize: 13, fontWeight: 500, 
                  cursor: (!file || loading) ? "not-allowed" : "pointer",
                  fontFamily: "'DM Sans', sans-serif", 
                  boxShadow: (!file || loading) ? "none" : "0 2px 10px rgba(191,61,12,0.2)",
                  transition: "all 0.15s",
                }}
              >
                Start Import
              </button>
            </>
          )}
        </div>

      </div>
    </div>
  );
}
